import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ListTodo, Plus, RefreshCw } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["hydration", "meal", "sleep", "movement", "social", "recovery", "rest"];

export default function Plan() {
  const t = useT();
  const qc = useQueryClient();
  const [newItem, setNewItem] = useState("");
  const [newCategory, setNewCategory] = useState("recovery");
  const [regenerating, setRegenerating] = useState(false);

  const plan = useQuery({
    queryKey: ["plan"],
    queryFn: async () => (await api.get("/plan")).data,
    retry: false,
  });

  const items = Array.isArray(plan.data)
    ? plan.data
    : plan.data?.items || plan.data?.plan || [];

  const toggle = async (item_id) => {
    if (!item_id) return;
    try {
      // Backend expects { item_id: string }
      await api.post("/plan/toggle", { item_id: String(item_id) });
      qc.invalidateQueries({ queryKey: ["plan"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const add = async () => {
    const title = newItem.trim();
    if (!title) return;
    try {
      // Backend expects { title, category (enum), time_hint? }
      await api.post("/plan/add", { title, category: newCategory });
      setNewItem("");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["plan"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const regen = async () => {
    setRegenerating(true);
    try {
      await api.post("/plan/regenerate");
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["plan"] });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRegenerating(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.plan")}
        title={t("client.yourPlan")}
        subtitle={t("brand.tagline")}
        actions={
          <Button
            onClick={regen}
            data-testid="plan-regenerate"
            disabled={regenerating}
            variant="outline"
            className="rounded-full border-border h-11"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" />
            {regenerating ? t("common.loading") : t("common.generate")}
          </Button>
        }
      />

      <div className="rounded-3xl bg-white shadow-card p-4 md:p-6">
        <div className="flex flex-wrap gap-2 mb-4">
          <Input
            data-testid="plan-add-input"
            placeholder="Add a small step…"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            className="rounded-xl bg-cream-50 h-11 flex-1 min-w-[200px]"
          />
          <Select value={newCategory} onValueChange={setNewCategory}>
            <SelectTrigger data-testid="plan-add-category" className="rounded-xl bg-cream-50 h-11 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={add} data-testid="plan-add-submit" className="rounded-full bg-sage-500 hover:bg-sage-600 h-11 px-5">
            <Plus className="h-4 w-4 mr-1" /> {t("common.add")}
          </Button>
        </div>

        {plan.isLoading && <LoadingCard height="h-16" />}
        {plan.isError && <ErrorState message={errMsg(plan.error)} onRetry={() => plan.refetch()} />}
        {!plan.isLoading && !plan.isError && items.length === 0 && (
          <EmptyState
            icon={ListTodo}
            title={t("common.empty")}
            description={t("client.yourPlan")}
            testId="plan-empty"
          />
        )}
        <ul className="space-y-2" data-testid="plan-list">
          {items.map((it, i) => {
            const itemId = it.id || it._id || it.item_id;
            return (
              <li
                key={itemId || i}
                className="flex items-center gap-3 rounded-2xl border border-border bg-cream-50 hover:bg-white transition p-3"
              >
                <Checkbox
                  checked={!!(it.done || it.completed || it.checked)}
                  onCheckedChange={() => toggle(itemId)}
                  data-testid={`plan-check-${i}`}
                />
                <div className="flex-1">
                  <div className={`text-ink-900 ${(it.done || it.completed) ? "line-through text-ink-500" : ""}`}>
                    {it.title || it.text || it.step || "(untitled)"}
                  </div>
                  {(it.category || it.time_hint) && (
                    <div className="text-xs text-ink-500 mt-0.5">
                      {[it.category, it.time_hint].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
